import React, { useState, useContext, useEffect } from "react";
import api from "../context/api";
import { AuthContext } from "../context/AuthContext";
import styled, { keyframes } from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import MtnLogo from "../Images/MTN.jpeg";
import VodafoneLogo from "../Images/Telecel-Cash-Logo.jpg";
import AirtelTigoLogo from "../Images/airteltigo_.jpg";

// Paystack mobile money provider codes for withdrawals:
// MTN -> "mtn", ATMoney & Airtel Money -> "atl", Telecel -> "vod".
const paymentMethods = [
  { name: "MTN", logo: MtnLogo, provider: "mtn" },
  { name: "VOD", logo: VodafoneLogo, provider: "vod" },
  { name: "ATL", logo: AirtelTigoLogo, provider: "atl" },
];

export const Withdrawal = () => {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [detectedNetwork, setDetectedNetwork] = useState(null);
  // winBalance is kept live by the socket listener in AuthContext.
  const { token, winBalance, fetchUserData, fullName } = useContext(AuthContext);

  // The withdrawal account holds winnings only — deposits and referral
  // commissions live in the playing balance and are not withdrawable.
  const withdrawableWinnings = Number(winBalance) || 0;
  const MINIMUM_WITHDRAWAL = 10;
  const hasWinnings = withdrawableWinnings > 0;

  const normalizeGhanaPhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.startsWith("0") && digits.length === 10) return digits;
    if (digits.startsWith("233") && digits.length === 12) return `0${digits.slice(3)}`;
    if (digits.length === 9) return `0${digits}`;
    return digits;
  };

  const detectNetworkFromPhone = (raw) => {
    const local = normalizeGhanaPhone(raw);
    if (!/^0\d{9}$/.test(local)) return null;

    const prefix = local.slice(0, 3);
    const prefixMap = {
      mtn: ["024", "025", "053", "054", "055", "059"],
      vod: ["020", "050"],
      atl: ["026", "027", "056", "057"],
    };

    if (prefixMap.mtn.includes(prefix)) return "mtn";
    if (prefixMap.vod.includes(prefix)) return "vod";
    if (prefixMap.atl.includes(prefix)) return "atl";
    return null;
  };

  useEffect(() => {
    const fetchPhoneNumber = async () => {
      try {
        const response = await api.get("/user/phoneNumber");
        setPhoneNumber(response.data.phoneNumber);
      } catch (error) {
        console.error("Error fetching phone number:", error);
      }
    };

    fetchPhoneNumber();
  }, []);

  // Refresh winnings on the same 2s cadence the wallet chip uses for the
  // deposit balance, so the withdrawable figure keeps up even when a socket
  // push is missed. Only runs while this page is open.
  useEffect(() => {
    const refresh = () => fetchUserData();
    refresh();
    const intervalId = setInterval(refresh, 2000);
    return () => clearInterval(intervalId);
  }, [fetchUserData]);

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    setDropdownVisible(false);
  };

  useEffect(() => {
    const detected = detectNetworkFromPhone(phoneNumber);
    setDetectedNetwork(detected);

    if (!paymentMethod && detected) {
      const method = paymentMethods.find((item) => item.provider === detected);
      if (method) {
        setPaymentMethod(method);
      }
    }
  }, [phoneNumber]);

  const handleSubmit = async () => {
    if (!hasWinnings) {
      return toast.error("You have no winnings to withdraw. Win a game first.");
    }

    const requestedAmount = parseFloat(amount);
    if (!Number.isFinite(requestedAmount) || requestedAmount <= 0) {
      return toast.error("Please enter a valid amount");
    }
    if (requestedAmount > withdrawableWinnings) {
      return toast.error("Amount exceeds your withdrawable winnings");
    }
    if (requestedAmount < MINIMUM_WITHDRAWAL) {
      return toast.error("Amount less than 10 cedis not accepted");
    }

    if (!paymentMethod) return toast.error("Please select a payment method");

    if (detectedNetwork && paymentMethod.provider !== detectedNetwork) {
      return toast.error("Selected network does not match this phone number. Please choose the detected network.");
    }

    const requestData = {
      RecipientName: fullName,
      RecipientMsisdn: phoneNumber,
      CustomerEmail: `${phoneNumber}@example.com`,
      Amount: parseFloat(amount),
      Description: "Withdrawal from Huntex",
      // Use Paystack provider code (mtn/atl/vod) as bank_code
      Channel: paymentMethod.provider,
      ClientReference: `WITHDRAWAL-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      RecipientName: fullName,
    };

    if (loading) return; // guards a double click on this device

    try {
      setLoading(true);
      const response = await api.post("/withdrawal", requestData);
      if (response.status === 200) {
        toast.success(response.data?.message || "Payment processed successfully.");
        setAmount("");
        await fetchUserData();
      } else {
        toast.error(response.data.message || "Payment Failed");
      }
    } catch (error) {
      if (error.response) {
        const data = error.response.data || {};
        // Prefer the human-readable message; `details` is a raw gateway payload.
        const msg =
          data.message || data.error || data.details || error.message || "Withdrawal failed. Please try again.";
        toast.error(typeof msg === "string" ? msg : "Withdrawal failed. Please try again.");

        // A rejected concurrent/duplicate attempt means the balance on this
        // device may be stale — resync it.
        if (error.response.status === 409) {
          await fetchUserData();
        }
      } else {
        toast.error("Network request failed. Check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <Header>Withdrawal Account</Header>
      <FormCard>
        <Card $empty={!hasWinnings}>
          <CardTitle>Win Balance</CardTitle>
          <CardContent>GH₵ {withdrawableWinnings.toFixed(2)}</CardContent>
        </Card>
        {!hasWinnings && (
          <EmptyNotice>
            You have no winnings yet. Only money won from a game can be withdrawn.
            Your deposit balance is for playing.
          </EmptyNotice>
        )}
        <InputLabel>Amount: (GH₵)</InputLabel>
        <Input
          type="number"
          placeholder="Enter amount to withdraw"
          value={amount}
          disabled={!hasWinnings}
          onChange={(e) => setAmount(e.target.value)}
        />
        <InputLabel>Mobile Number</InputLabel>
        <Input type="text" placeholder="Enter your mobile number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
        {detectedNetwork && (
          <HelperText>
            Detected network: <strong>{detectedNetwork.toUpperCase()}</strong>
          </HelperText>
        )}
        {detectedNetwork && paymentMethod && paymentMethod.provider !== detectedNetwork && (
          <WarningText>
            Selected method does not match the detected network.
          </WarningText>
        )}
        <InputLabel>Payment Method</InputLabel>
        <DropdownWrapper>
          <DropdownButton onClick={() => setDropdownVisible(!dropdownVisible)}>
            {paymentMethod ? (
              <>
                <img src={paymentMethod.logo} alt={paymentMethod.name} width={40} />
                <span>{paymentMethod.name}</span>
              </>
            ) : (
              "Select Payment Method"
            )}
          </DropdownButton>
          {dropdownVisible && (
            <DropdownMenu>
              {paymentMethods.map((method, index) => (
                <DropdownItem key={index} onClick={() => handlePaymentMethodSelect(method)}>
                  <img src={method.logo} alt={method.name} width={40} />
                  {method.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownWrapper>
        <SubmitButton onClick={handleSubmit} disabled={loading || !hasWinnings}>
          {loading ? "Processing..." : hasWinnings ? "Withdraw" : "No Winnings to Withdraw"}
        </SubmitButton>
      </FormCard>
    </Container>
  );
};

const slideDown = keyframes`
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #134b5f 0%, #1a5f73 100%);
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.h1`
  color: #ffffff;
  margin-bottom: 40px;
  font-size: 32px;
  font-weight: bold;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const FormCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  animation: ${slideDown} 0.5s ease-in-out;
`;

const Card = styled.div`
  background: ${({ $empty }) =>
    $empty
      ? "linear-gradient(135deg, #637f88, #4b5563)"
      : "linear-gradient(135deg, #03750b, #025a08)"};
  padding: 24px;
  text-align: center;
  margin-bottom: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%);
    pointer-events: none;
  }
`;

const CardTitle = styled.h2`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const CardContent = styled.p`
  font-size: 36px;
  font-weight: bold;
  color: #ffffff;
  position: relative;
  z-index: 1;
`;

const CardNote = styled.p`
  margin-top: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  position: relative;
  z-index: 1;
`;

const EmptyNotice = styled.p`
  margin: 0 0 8px;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 14px;
  line-height: 1.5;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  margin-top: 20px;
`;

const Input = styled.input`
  width: 90%;
  padding: 14px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:focus {
    border-color: #03750b;
    outline: none;
    background-color: #ffffff;
    box-shadow: 0 4px 8px rgba(3, 117, 11, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const HelperText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #166534;
`;

const WarningText = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: #b91c1c;
  font-weight: 600;
`;

const DropdownWrapper = styled.div`
  width: 100%;
  margin-top: 20px;
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 14px 16px;
  background: #fafafa;
  color: #333;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-align: left;

  &:hover {
    border-color: #03750b;
    background: #ffffff;
    box-shadow: 0 4px 8px rgba(3, 117, 11, 0.1);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  overflow: hidden;
  animation: ${slideDown} 0.3s ease-in-out;
`;

const DropdownItem = styled.div`
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fdf9;
    padding-left: 20px;
  }
  
  img {
    border-radius: 4px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #03750b, #025a08);
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 32px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(3, 117, 11, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }

  &:hover::before {
    width: 300px;
    height: 300px;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(3, 117, 11, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    box-shadow: none;
    
    &:hover {
      transform: none;
    }
  }
`;

export default Withdrawal;
